import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import type { Tenant } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useTenant } from '../../auth/TenantContext';
import { useIsWorkspaceAdmin } from '../../auth/useWorkspaceAdmin';
import { Card, CardBody, CardHeader } from '../ui/Card';
import { FormField, Input } from '../ui/Field';
import { Button } from '../ui/Button';

/**
 * "Danger Zone" for the Edit Brand Workspace page: permanently delete this workspace. Guarded by
 * a type-to-confirm (the admin must type the workspace's exact name) plus Workspace-Admin gating
 * (the API is the authoritative gate — this hides the card for everyone else). On success the
 * tenant list is refreshed; TenantContext then auto-switches to another workspace (or the empty
 * gate), and we navigate to the dashboard.
 *
 * `canEdit` is the Brand Workspace v2 Danger zone page's rule (by the workspace being edited, E20);
 * without it, today's useIsWorkspaceAdmin decides, as on the old page.
 */
export function DeleteWorkspaceCard({
  workspace,
  canEdit: canEditProp,
}: {
  workspace: Tenant;
  /** Who may delete the workspace. Default: today's rule, useIsWorkspaceAdmin. */
  canEdit?: boolean;
}) {
  const isWorkspaceAdmin = useIsWorkspaceAdmin();
  const canEdit = canEditProp ?? isWorkspaceAdmin;
  const { refresh } = useTenant();
  const navigate = useNavigate();

  const [confirmName, setConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Members can't delete; the whole card is hidden for them (the API also 403s a non-admin).
  if (!canEdit) return null;

  // Exact-match confirmation (trimmed) — no accidental deletes from a fuzzy value.
  const confirmed = confirmName.trim() === workspace.name.trim();
  const canDelete = confirmed && !deleting;

  async function handleDelete() {
    if (!canDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await api.deleteTenant(workspace.id);
      // Drop the deleted workspace from the list; TenantContext switches the active workspace
      // (or engages the no-workspace gate) off the fresh list.
      await refresh();
      navigate('/');
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : 'Could not delete this workspace. Please try again.',
      );
      setDeleting(false);
    }
  }

  return (
    <Card className="border-red-200 dark:border-error-dark/30">
      <CardHeader
        title="Danger Zone"
        description="Permanently delete this Brand Workspace. This cannot be undone."
      />
      <CardBody className="space-y-4">
        <div className="flex gap-3 rounded-lg border border-red-200 dark:border-error-dark/30 bg-red-50 dark:bg-error-dark-tint px-4 py-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500 dark:text-error-dark" aria-hidden />
          <p className="text-sm leading-relaxed text-red-900 dark:text-error-dark">
            Deleting <strong>{workspace.name}</strong> permanently removes all of its content,
            campaigns, posts, sources, digests, assets, approvals, and member access. Other members
            will lose access immediately. <strong>This action cannot be undone.</strong>
          </p>
        </div>

        <FormField
          label={`Type “${workspace.name}” to confirm`}
          htmlFor="delete-workspace-confirm"
        >
          <Input
            id="delete-workspace-confirm"
            value={confirmName}
            autoComplete="off"
            placeholder={workspace.name}
            onChange={(e) => {
              setError(null);
              setConfirmName(e.target.value);
            }}
          />
        </FormField>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="danger"
            onClick={() => void handleDelete()}
            loading={deleting}
            disabled={!canDelete}
            title={confirmed ? undefined : 'Type the workspace name to enable deletion'}
          >
            Delete this Brand Workspace
          </Button>
          {error && <span className="text-sm text-red-600 dark:text-error-dark">{error}</span>}
        </div>
      </CardBody>
    </Card>
  );
}
